import { API_URL } from '../config/apiConfig';

/**
 * Fetch with retry logic and timeout protection
 * Handles network failures caused by mobile browsers suspending requests when page is backgrounded
 */
async function fetchWithRetry(
  url: string,
  options: RequestInit,
  maxRetries: number = 2,
  timeoutMs: number = 60000
): Promise<Response> {
  let lastError: Error | null = null;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      if (attempt > 0) {
        const waitTime = 2000 * attempt; // 2s, 4s
        await new Promise(resolve => setTimeout(resolve, waitTime));
      }
      
      // Add timeout protection with AbortController
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
      }, timeoutMs);
      
      try {
        const response = await fetch(url, {
          ...options,
          signal: controller.signal,
        });
        
        clearTimeout(timeoutId);
        return response;
        
      } catch (fetchError) {
        clearTimeout(timeoutId);
        throw fetchError;
      }
      
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));
      
      // Check if this was a timeout/abort
      if (lastError.name === 'AbortError') {
        console.error('❌ [VideoGen] Request was aborted (timeout or page backgrounded)');
        // Don't retry on timeout - need to fail fast
        throw new Error('Request timed out. Please ensure you stay on this page and try again.');
      }
      
      // Check for network errors (Load failed) - these are retryable
      if (lastError instanceof TypeError && lastError.message.includes('Load failed')) {
        if (attempt < maxRetries) {
          continue; // Retry
        } else {
          console.error('❌ [VideoGen] All retry attempts exhausted');
          throw new Error('Network request failed after multiple attempts. Please ensure you stay on this page and try again.');
        }
      }
      
      // For other errors, don't retry
      console.error('❌ [VideoGen] Non-retryable error:', lastError);
      throw lastError;
    }
  }
  
  // Should never reach here, but TypeScript needs this
  throw lastError || new Error('Failed to fetch after retries');
}

export interface ImageToVideoRequest {
  asset_id: string; // Asset ID from uploaded image
  image_url?: string; // Image URL (s3_url) - required by backend
  prompt?: string; // Enhanced prompt (from prompt enhancer, matches fal.ai)
  negative_prompt?: string; // Enhanced negative prompt (from prompt enhancer)
  email?: string; // User email (if authenticated)
  anon_id?: string; // Anonymous user ID (if not authenticated)
  model?: string; // Optional model identifier
  // Video duration in seconds (5 or 10). Omitted on i2v/Lander-1; only
  // homepage sends this when the user has selected 10s and is eligible.
  // Backend validates and enforces eligibility independently.
  duration?: 5 | 10;
}

export interface ImageToVideoRequestFromUrl {
  image_url: string; // Direct image URL
  prompt: string;
  email?: string; // User email (if authenticated)
  anon_id?: string; // Anonymous user ID (if not authenticated)
  model?: string; // Optional model identifier
}

export interface ImageToVideoResponse {
  asset_id: string; // Video asset ID (legacy)
  operation_id?: string; // Operation ID for new polling system (future)
  status: string;
  job_id?: string; // Replicate prediction ID
  message?: string;
}

/** Credit refund for a failed generation (backend contract §4.4). Absent on older API builds. */
export interface RefundInfo {
  refunded: boolean;
  amount: number;
  new_balance: number | null;
  already_refunded: boolean;
}

export interface JobStatus {
  status: 'pending' | 'processing' | 'completed' | 'failed';
  refund?: RefundInfo | null;
  progress?: number;
  videoUrl?: string;
  error?: string | { type: string; message?: string; current_credits?: number; required_credits?: number };
}

/**
 * Start an image-to-video generation job
 * Uses the backend endpoint that expects asset_id (from uploaded image)
 */
export async function startImageToVideoJob(
  request: ImageToVideoRequest
): Promise<ImageToVideoResponse> {
  const API_BASE_URL = API_URL;

  const requestBody: {
    asset_id: string;
    image_url?: string;
    prompt?: string;
    negative_prompt?: string;
    model: string;
    email?: string;
    anon_id?: string;
    duration?: 5 | 10;
  } = {
    asset_id: request.asset_id,
    model: request.model || 'wan/v2.6/image-to-video',
  };

  // Only forward duration when explicitly provided (homepage 10s opt-in).
  // Omitting falls through to backend default of 5 — preserves i2v /
  // Lander-1 / ad-handoff contract.
  if (request.duration !== undefined) {
    requestBody.duration = request.duration;
  }

  // Use enhanced prompts if available
  if (request.prompt) {
    requestBody.prompt = request.prompt;
  } else {
    throw new Error('Prompt is required');
  }
  
  if (request.negative_prompt) {
    requestBody.negative_prompt = request.negative_prompt;
  }

  // Add image_url if provided (required by backend)
  if (request.image_url) {
    requestBody.image_url = request.image_url;
  }

  // Add user identification
  if (request.email) {
    requestBody.email = request.email;
  } else if (request.anon_id) {
    requestBody.anon_id = request.anon_id;
  }
  
  const response = await fetchWithRetry(
    `${API_BASE_URL}/api/generate/video`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    },
    2, // Max 2 retries
    60000 // 60 second timeout
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to start job' }));
    console.error('❌ [VideoGen] Request failed:', error);
    
    // Handle 402 Payment Required (insufficient credits)
    if (response.status === 402) {
      const creditError = {
        type: 'insufficient_credits' as const,
        message: error.message || error.error || 'Insufficient credits',
        current_credits: error.current_credits ?? 0,
        required_credits: error.required_credits ?? 100,
      };
      throw creditError;
    }
    
    // Prefer the backend's user-ready `message` (built from the failure class and
    // whether the refund landed) over `error`, which is a machine-ish label —
    // showing `error` is why users saw a bare "Failed to start generation".
    throw new Error(error.message || error.error || 'Failed to start image-to-video job');
  }

  const result = await response.json();
  
  return {
    asset_id: result.asset_id,
    operation_id: result.operation_id, // Support new system if backend provides it
    status: result.status,
    job_id: result.job_id,
    message: result.message,
  };
}

/**
 * Start an image-to-video generation job using direct image URL
 * This is used for the lander-1 page which only takes image URL as input
 */
export async function startImageToVideoJobFromUrl(
  request: ImageToVideoRequestFromUrl
): Promise<ImageToVideoResponse> {
  const API_BASE_URL = API_URL;

  const requestBody: {
    image_url: string;
    prompt: string;
    model: string;
    email?: string;
    anon_id?: string;
  } = {
    image_url: request.image_url,
    prompt: request.prompt,
    model: request.model || 'wan/v2.6/image-to-video',
  };

  // Add user identification
  if (request.email) {
    requestBody.email = request.email;
  } else if (request.anon_id) {
    requestBody.anon_id = request.anon_id;
  }
  
  const response = await fetchWithRetry(
    `${API_BASE_URL}/api/generate/video`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    },
    2, // Max 2 retries
    60000 // 60 second timeout
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to start job' }));
    console.error('❌ [VideoGen] Request failed:', error);
    
    // Handle 402 Payment Required (insufficient credits)
    if (response.status === 402) {
      const creditError = {
        type: 'insufficient_credits' as const,
        message: error.message || error.error || 'Insufficient credits',
        current_credits: error.current_credits ?? 0,
        required_credits: error.required_credits ?? 100,
      };
      throw creditError;
    }
    
    // Same precedence as startImageToVideoJob: the backend's user-ready `message` first.
    throw new Error(error.message || error.error || 'Failed to start image-to-video job');
  }

  const result = await response.json();
  
  return {
    asset_id: result.asset_id,
    operation_id: result.operation_id, // Support new system if backend provides it
    status: result.status,
    job_id: result.job_id,
    message: result.message,
  };
}

/**
 * Check asset directly from Supabase as fallback when backend status fails
 * Exported for use in visibility change handlers
 */
export async function checkAssetFromSupabase(assetId: string): Promise<JobStatus | null> {
  try {
    const { getAsset } = await import('./assets');
    const asset = await getAsset(assetId);
    
    if (!asset) {
      return null;
    }
    
    const videoUrl = asset.video_url;
    const generationStatus = asset.status || (asset as any).generation_status;
    
    // If asset has video_url and is completed, return completed status
    if (videoUrl && (generationStatus === 'completed' || asset.status === 'completed')) {
      return {
        status: 'completed' as const,
        progress: 100,
        videoUrl: videoUrl,
        error: undefined,
      };
    }
    
    // If asset is marked as failed.
    // This path reads Supabase directly (anon key) and so has no access to the
    // credit ledger — it cannot know whether the refund landed. Policy is no video =
    // no charge and the backend refunds at every failure edge, so say the credits are
    // back without a specific amount, and let the balance refresh prove it.
    if (generationStatus === 'failed' || asset.status === 'failed') {
      return {
        status: 'failed' as const,
        progress: undefined,
        videoUrl: undefined,
        error: "We couldn't generate your video. Your credits have been refunded, please try again.",
        refund: null,
      };
    }
    
    // Asset exists but not completed yet
    return {
      status: 'processing' as const,
      progress: undefined,
      videoUrl: undefined,
      error: undefined,
    };
  } catch (error) {
    console.error('❌ [VideoGen] Error checking asset from Supabase:', error);
    return null;
  }
}

/**
 * Poll job status by querying backend API
 * Uses the correct endpoint: /api/generate/status with both job_id and asset_id
 * Falls back to checking asset directly from Supabase if backend fails
 */
export async function pollJob(jobId: string, assetId: string): Promise<JobStatus> {
  const API_BASE_URL = API_URL;
  
  try {
    // Use correct endpoint with both job_id and asset_id (required for backend to find RunPod job)
    const statusUrl = `${API_BASE_URL}/api/generate/status?job_id=${jobId}&asset_id=${assetId}`;
    
    const response = await fetch(statusUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      if (response.status === 404) {
        // Job might not exist yet (race condition) - check Supabase as fallback
        const fallbackStatus = await checkAssetFromSupabase(assetId);
        if (fallbackStatus) {
          return fallbackStatus;
        }
        // If fallback also doesn't find it, return pending
        return {
          status: 'pending' as const,
          progress: undefined,
          videoUrl: undefined,
          error: undefined,
        };
      }
      
      // For 500 errors or other failures, try Supabase fallback
      if (response.status === 500) {
        const fallbackStatus = await checkAssetFromSupabase(assetId);
        if (fallbackStatus) {
          return fallbackStatus;
        }
      }
      
      const error = await response.json().catch(() => ({ error: 'Failed to fetch job status' }));
      console.error('❌ [VideoGen] Status check failed:', error);
      throw new Error(error.error || error.message || 'Failed to fetch job status');
    }
    
    const data = await response.json();
    
    // Extract status and video URL from response
    // Backend returns: { success, job_id, status, generation_status, video_url, s3_url, watermarked_video_url }
    const generationStatus = data.generation_status || data.status || 'processing';
    // Check for watermarked URL first (default for non-subscribers), then original URL
    const videoUrl = data.watermarked_video_url || data.video_url || data.s3_url || data.runpod_url;
    
    // Log critical issues only
    if (generationStatus === 'completed' && !videoUrl) {
      console.warn('⚠️ [VideoGen] Status is completed but video_url is missing');
    }
    
    // Determine status from backend response
    let status: 'pending' | 'processing' | 'completed' | 'failed' = 'processing';
    
    if (generationStatus === 'completed' || data.status === 'COMPLETED') {
      // CRITICAL: Only mark as completed if we have a video URL
      // If status is 'completed' but no videoUrl, treat as 'processing' to continue polling
      if (videoUrl) {
        status = 'completed';
      } else {
        status = 'processing';
      }
    } else if (generationStatus === 'failed' || data.status === 'FAILED') {
      status = 'failed';
    } else if (generationStatus === 'processing' || data.status === 'IN_PROGRESS' || data.status === 'IN_QUEUE') {
      status = 'processing';
    } else {
      status = 'pending';
    }
    
    // Preserve credit error information if present
    let error: string | { type: string; message?: string; current_credits?: number; required_credits?: number } | undefined = undefined;
    if (status === 'failed') {
      // Check if backend returned structured error with credit information
      if (data.error && typeof data.error === 'object' && data.error !== null) {
        // Backend returned structured error (e.g., insufficient credits)
        error = data.error;
      } else if (data.error_type === 'insufficient_credits' || 
                 (data.error && typeof data.error === 'string' && 
                  (data.error.toLowerCase().includes('insufficient credits') || 
                   data.error.toLowerCase().includes('not enough credits')))) {
        // Backend returned credit error in different format - construct structured error
        error = {
          type: 'insufficient_credits',
          message: data.error || data.error_message || 'Insufficient credits',
          current_credits: data.current_credits ?? 0,
          required_credits: data.required_credits ?? 100,
        };
      } else {
        // Generic error. `message` is the backend's user-ready sentence (includes the
        // "credits refunded" clause when a refund actually landed); `error` is the raw
        // provider text and is the fallback for API builds without `message`.
        error = data.message || data.error || data.error_message || 'Video generation failed';
      }
    }
    
    return {
      status,
      progress: data.progress !== undefined ? data.progress : undefined,
      videoUrl: videoUrl,
      error: error,
      refund: data.refund ?? null,
    };
  } catch (error) {
    // Log different error types for better debugging
    const errorMessage = error instanceof Error ? error.message : String(error);
    const isNetworkError = errorMessage.includes('Load failed') || 
                          errorMessage.includes('Network request failed') ||
                          errorMessage.includes('Failed to fetch');
    
    if (isNetworkError) {
      console.warn('⚠️ [VideoGen] Network error during poll (page may be hidden), trying Supabase fallback:', errorMessage);
    } else {
      console.error('❌ [VideoGen] Poll failed, trying Supabase fallback:', errorMessage);
    }
    
    // When backend polling fails, check asset directly from Supabase
    // This handles cases where:
    // 1. RunPod job is gone but video exists in Supabase
    // 2. Network is unstable (page backgrounded, mobile data issues)
    // 3. Backend is temporarily unavailable
    const fallbackStatus = await checkAssetFromSupabase(assetId);
    if (fallbackStatus) {
      console.log('✅ [VideoGen] Supabase fallback succeeded:', fallbackStatus.status);
      return fallbackStatus;
    }
    
    // If fallback also fails, throw the original error
    throw new Error(error instanceof Error ? error.message : 'Failed to fetch asset status');
  }
}

/**
 * Mock job simulation for offline mode
 */
export function simulateImageToVideoJob(
  onProgress: (progress: number) => void,
  onComplete: (jobId: string, videoUrl: string) => void
): () => void {
  const jobId = `mock-job-${Date.now()}`;
  let progress = 0;
  const interval = setInterval(() => {
    progress += Math.random() * 15 + 5; // Increment by 5-20%
    if (progress >= 100) {
      progress = 100;
      clearInterval(interval);
      // Use a mock video URL (blob or public file)
      const videoUrl = '/mock/video.mp4';
      onComplete(jobId, videoUrl);
    } else {
      onProgress(Math.min(progress, 100));
    }
  }, 800); // Update every 800ms for ~8 second total

  return () => clearInterval(interval);
}

